import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCallStateHooks, Video } from "@stream-io/video-react-sdk";
import { motion } from "framer-motion";
import { UserAvatar } from "@/features/chat/components/UserAvatar";

interface CallGridProps {
  raisedHands?: Set<string>;
  stagedUserId?: string | null;
  onStage?: (userId: string | null) => void;
  isCreator?: boolean;
}

export const CallGrid = ({
  raisedHands,
  stagedUserId,
  onStage,
  isCreator,
}: CallGridProps) => {
  const { t } = useTranslation();
  const {
    useRemoteParticipants,
    useLocalParticipant,
    useDominantSpeaker,
  } = useCallStateHooks();
  const remoteParticipants = useRemoteParticipants();
  const localParticipant = useLocalParticipant();
  const dominantSpeaker = useDominantSpeaker();

  const allParticipants = localParticipant
    ? [localParticipant, ...remoteParticipants]
    : remoteParticipants;

  const manualStaged = stagedUserId
    ? allParticipants.find((p) => p.userId === stagedUserId) ?? null
    : null;

  // Auto-stage dominant speaker when nothing is manually pinned,
  // only in group calls (3+ participants) and never the local user.
  const autoStaged =
    !manualStaged && allParticipants.length >= 3 && dominantSpeaker
      ? allParticipants.find(
          (p) =>
            p.sessionId === dominantSpeaker.sessionId &&
            p.sessionId !== localParticipant?.sessionId,
        ) ?? null
      : null;

  const staged = manualStaged ?? autoStaged;
  const isManualStage = !!manualStaged;

  // Stage layout: one large participant + filmstrip
  if (staged) {
    const filmstrip = allParticipants.filter((p) => p.userId !== staged.userId);
    const isLocal = staged.sessionId === localParticipant?.sessionId;

    return (
      <div className="flex flex-col h-full w-full gap-2">
        {/* Main stage */}
        <div className="flex-1 min-h-0 relative rounded-xl overflow-hidden bg-slate-800">
          <ParticipantTile
            participant={staged}
            isSpeaking={dominantSpeaker?.sessionId === staged.sessionId}
            isLocal={isLocal}
            hasRaisedHand={raisedHands?.has(staged.userId) ?? false}
          />
          {/* Stage badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-600/80 backdrop-blur-sm text-white text-xs font-medium">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isManualStage ? "M5 3l14 9-14 9V3z" : "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"} />
            </svg>
            {isManualStage ? t("call.on_stage") : t("call.speaking")}
          </div>
          {/* Remove from stage — creator only, manual stage only */}
          {isCreator && isManualStage && (
            <button
              onClick={() => onStage?.(null)}
              className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs hover:bg-black/70 transition-colors backdrop-blur-sm"
              title={t("call.remove_from_stage")}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t("call.remove_from_stage")}
            </button>
          )}
        </div>

        {/* Filmstrip */}
        {filmstrip.length > 0 && (
          <div className="h-20 flex gap-2 px-1 overflow-x-auto flex-shrink-0">
            {filmstrip.map((p) => (
              <FilmstripTile
                key={p.sessionId}
                participant={p}
                isLocal={p.sessionId === localParticipant?.sessionId}
                isSpeaking={dominantSpeaker?.sessionId === p.sessionId}
                hasRaisedHand={raisedHands?.has(p.userId) ?? false}
                isCreator={isCreator}
                onStage={onStage}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default grid layout
  const count = allParticipants.length;
  const cols = count <= 1 ? 1 : count <= 4 ? 2 : 3;

  return (
    <div
      className="h-full w-full p-2 gap-2"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridAutoRows: "1fr",
      }}
    >
      {allParticipants.map((p) => (
        <StagableTile
          key={p.sessionId}
          participant={p}
          isSpeaking={dominantSpeaker?.sessionId === p.sessionId}
          isLocal={p.sessionId === localParticipant?.sessionId}
          hasRaisedHand={raisedHands?.has(p.userId) ?? false}
          isCreator={isCreator}
          onStage={onStage}
        />
      ))}
    </div>
  );
};

// Grid tile with "Put on stage" hover button for the creator
const StagableTile = ({
  participant,
  isSpeaking,
  isLocal,
  hasRaisedHand,
  isCreator,
  onStage,
}: {
  participant: any;
  isSpeaking: boolean;
  isLocal: boolean;
  hasRaisedHand: boolean;
  isCreator?: boolean;
  onStage?: (userId: string | null) => void;
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      layout
      className={`relative rounded-xl overflow-hidden bg-slate-800 transition-shadow duration-300 ${
        isSpeaking
          ? "ring-2 ring-indigo-500 ring-offset-2 ring-offset-slate-900 shadow-[0_0_20px_rgba(99,102,241,0.3)]"
          : ""
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ParticipantTile
        participant={participant}
        isSpeaking={isSpeaking}
        isLocal={isLocal}
        hasRaisedHand={hasRaisedHand}
      />

      {/* Put on stage button — creator hover, not shown on local tile */}
      {isCreator && !isLocal && hovered && (
        <button
          onClick={() => onStage?.(participant.userId)}
          className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-600/80 text-white text-[11px] font-medium hover:bg-indigo-600 transition-colors backdrop-blur-sm"
          title={t("call.put_on_stage")}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
          </svg>
          {t("call.put_on_stage")}
        </button>
      )}
    </motion.div>
  );
};

// Compact filmstrip tile used in stage layout
const FilmstripTile = ({
  participant,
  isLocal,
  isSpeaking,
  hasRaisedHand,
  isCreator,
  onStage,
}: {
  participant: any;
  isLocal: boolean;
  isSpeaking: boolean;
  hasRaisedHand: boolean;
  isCreator?: boolean;
  onStage?: (userId: string | null) => void;
}) => {
  const { t } = useTranslation();
  const hasVideo = participant.publishedTracks?.includes(2);

  return (
    <div
      className={`relative w-24 h-full rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 cursor-pointer group ${
        isSpeaking ? "ring-2 ring-indigo-500" : ""
      }`}
      onClick={() => isCreator && !isLocal && onStage?.(participant.userId)}
      title={isCreator && !isLocal ? t("call.put_on_stage") : undefined}
    >
      {hasVideo ? (
        <Video
          participant={participant}
          trackType="videoTrack"
          mirror={isLocal}
          className="w-full h-full object-cover"
          VideoPlaceholder={() => <FilmstripPlaceholder participant={participant} />}
        />
      ) : (
        <FilmstripPlaceholder participant={participant} />
      )}

      {/* Stage-on-click hint overlay for creator */}
      {isCreator && !isLocal && (
        <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/20 transition-colors flex items-center justify-center">
          <svg className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
          </svg>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5">
        <p className="text-white text-[10px] font-medium truncate">
          {isLocal ? t("call.you") : participant.name || participant.userId}
        </p>
      </div>

      {hasRaisedHand && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center text-[11px]">
          ✋
        </div>
      )}
    </div>
  );
};

const ParticipantTile = ({
  participant,
  isSpeaking,
  isLocal,
  hasRaisedHand,
}: {
  participant: any;
  isSpeaking: boolean;
  isLocal: boolean;
  hasRaisedHand: boolean;
}) => {
  const { t } = useTranslation();
  const hasVideo = participant.publishedTracks?.includes(2);

  return (
    <div className="w-full h-full">
      {hasVideo ? (
        <Video
          participant={participant}
          trackType="videoTrack"
          mirror={isLocal}
          className="w-full h-full object-cover"
          VideoPlaceholder={() => (
            <AvatarPlaceholder participant={participant} />
          )}
        />
      ) : (
        <AvatarPlaceholder participant={participant} />
      )}

      {hasRaisedHand && (
        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-amber-400/90 flex items-center justify-center text-lg z-10 animate-bounce">
          <span role="img" aria-label="Raised hand">✋</span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-center gap-2">
        {isSpeaking && (
          <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse flex-shrink-0" />
        )}
        <p className="text-white text-xs font-medium truncate">
          {isLocal ? t("call.you") : participant.name || participant.userId}
        </p>
        {participant.audioStream === undefined && (
          <svg
            className="w-3 h-3 text-red-400 ml-auto flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
            />
          </svg>
        )}
      </div>
    </div>
  );
};

const AvatarPlaceholder = ({ participant }: { participant: any }) => (
  <div className="w-full h-full flex items-center justify-center bg-slate-800">
    <UserAvatar
      name={participant.name || participant.userId}
      id={participant.userId}
      image={participant.image}
      size={80}
    />
  </div>
);

const FilmstripPlaceholder = ({ participant }: { participant: any }) => (
  <div className="w-full h-full flex items-center justify-center bg-slate-800">
    <UserAvatar
      name={participant.name || participant.userId}
      id={participant.userId}
      image={participant.image}
      size={32}
    />
  </div>
);
